import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import Slide from "@mui/material/Slide";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";

import { SchemesV3Api, TargetGroupTemplatesV3Api } from "apis/SchemesV3Api";
import { GroupsApi } from "apis/GroupsApi";
import { GetRetailerTypes } from "apis/RetailerApi";
import AutoComplete from "components/AutoComplete";
import { showToast } from "utils";
import TargetGroupBuilder, { buildAttributeFilters, mapAttributeFiltersToUi } from "./TargetGroupBuilder";
import ConfigBuilder from "./ConfigBuilder";
import { getInitialSchemePayload, getInitialTargetGroup, getInitialConfig } from "./constants";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const mode = (scheme) => {
  if (!scheme || !scheme.id) return "create";
  if (scheme.status === "ARCHIVED") return "view";
  if (scheme.status === "ACTIVE") return "amend";
  return "edit"; // DRAFT / IN_REVIEW → full PUT replace
};

export default function SchemeFormModal({ open, onClose, brands, defaultPeriod, editingScheme, onSaved }) {
  const [payload, setPayload] = useState(getInitialSchemePayload(defaultPeriod.periodFrom, defaultPeriod.periodTo));
  const [templates, setTemplates] = useState([]);
  const [retailerTypes, setRetailerTypes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (editingScheme) {
      const s = editingScheme;
      setPayload({
        id: s.id,
        name: s.name || "",
        description: s.description || "",
        imageUrl: s.imageUrl || "",
        termsAndConditions: s.termsAndConditions || "",
        brandId: s.brand?.id || s.brandId || null,
        brandName: s.brand?.name || "",
        periodFrom: s.periodFrom || defaultPeriod.periodFrom,
        periodTo: s.periodTo || defaultPeriod.periodTo,
        groupIds: (s.groups || []).map((g) => g.id),
        groups: s.groups || [],
        retailerTypeId: s.retailerType?.id || "",
        retailerTypeName: s.retailerType?.name || "",
        targetGroups: (s.targetGroups || []).length
          ? s.targetGroups.map((tg) => ({
              id: tg.id,
              name: tg.name || "",
              templateId: tg.templateId || "",
              productIds: tg.productIds || [],
              products: (tg.products || []).length
                ? tg.products
                : (tg.productIds || []).map((id) => ({ id, primaryName: id, secondaryName: "" })),
              attributeFilters: mapAttributeFiltersToUi(tg.attributeFilters),
            }))
          : [getInitialTargetGroup()],
        configs: (s.configs || []).length
          ? s.configs.map((c) => ({
              name: c.name || "",
              targetGroupId: c.targetGroupId || "",
              targetAppliedOn: c.targetAppliedOn || "INDIVIDUAL_MODEL",
              achievementBasis: c.achievementBasis || "SELL_OUT",
              payoutBasis: c.payoutBasis || "SELL_OUT",
              payoutType: c.payoutType || "PER_UNIT_AMOUNT",
              periodFrom: c.periodFrom || "",
              periodTo: c.periodTo || "",
              helpText: c.helpText || "",
              protection: c.protection || { claimWindowDays: "", brandStatedClaimValuePerUnit: "" },
              slabs: (c.slabs || []).length
                ? c.slabs.map((sl) => ({
                    rangeFrom: sl.rangeFrom ?? "",
                    rangeTo: sl.rangeTo ?? "",
                    amount: sl.amount ?? "",
                    gift: sl.gift || "",
                    remark: sl.remark || "",
                    periodFrom: sl.periodFrom || "",
                    periodTo: sl.periodTo || "",
                    isDefaultSlab: !!sl.isDefaultSlab,
                  }))
                : [{ rangeFrom: "", rangeTo: "", amount: "", gift: "", remark: "", periodFrom: "", periodTo: "", isDefaultSlab: false }],
            }))
          : [getInitialConfig()],
        status: s.status || "DRAFT",
        publishVersion: s.publishVersion ?? null,
      });
      if (s.brand?.id || s.brandId) fetchTemplates(s.brand?.id || s.brandId);
      fetchRetailerTypes(s.brand?.id || s.brandId);
    } else {
      setPayload(getInitialSchemePayload(defaultPeriod.periodFrom, defaultPeriod.periodTo));
      setTemplates([]);
      setRetailerTypes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingScheme]);

  const fetchTemplates = (brandId) => {
    if (!brandId) return;
    TargetGroupTemplatesV3Api.GetByFilter(brandId)
      .then((res) => setTemplates(res.data?.data || []))
      .catch(() => setTemplates([]));
  };

  const fetchRetailerTypes = (brandId) => {
    if (!brandId) return;
    GetRetailerTypes({ page: 0, size: 100, brandId })
      .then((res) => setRetailerTypes(res.data?.data || []))
      .catch(() => setRetailerTypes([]));
  };

  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    const brand = brands.find((b) => b.id === brandId);
    setPayload((p) => ({ ...p, brandId, brandName: brand?.name || "", retailerTypeId: "", retailerTypeName: "" }));
    fetchTemplates(brandId);
    fetchRetailerTypes(brandId);
  };

  const fetchGroups = async (keyword) => {
    try {
      const { data } = await GroupsApi.GetGroupsListBySearch({ keyword, page: 0, size: 50 });
      return data.data || [];
    } catch (err) {
      showToast(err?.response?.data?.message || "Error fetching groups");
      return [];
    }
  };

  const handleGroupsChange = (_, value) => {
    setPayload((p) => ({ ...p, groupIds: value.map((g) => g.id), groups: value }));
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const updateTargetGroup = (index, tg) => {
    const targetGroups = [...payload.targetGroups];
    targetGroups[index] = tg;
    setPayload((p) => ({ ...p, targetGroups }));
  };

  const addTargetGroup = () =>
    setPayload((p) => ({ ...p, targetGroups: [...p.targetGroups, getInitialTargetGroup()] }));

  const removeTargetGroup = (index) =>
    setPayload((p) => ({ ...p, targetGroups: p.targetGroups.filter((_, i) => i !== index) }));

  const updateConfig = (index, cfg) => {
    const configs = [...payload.configs];
    configs[index] = cfg;
    setPayload((p) => ({ ...p, configs }));
  };

  const addConfig = () => setPayload((p) => ({ ...p, configs: [...p.configs, getInitialConfig()] }));
  const removeConfig = (index) => setPayload((p) => ({ ...p, configs: p.configs.filter((_, i) => i !== index) }));

  const buildRequestPayload = () => {
    let attrErr = null;
    const targetGroups = payload.targetGroups.map((tg) => {
      let attributeFilters;
      try {
        attributeFilters = buildAttributeFilters(tg.attributeFilters);
      } catch (err) {
        attrErr = err.message;
        attributeFilters = undefined;
      }
      const body = { name: tg.name };
      if (tg.templateId) body.templateId = tg.templateId;
      if (tg.productIds?.length) body.productIds = tg.productIds;
      if (attributeFilters) body.attributeFilters = attributeFilters;
      return body;
    });
    if (attrErr) throw new Error(attrErr);

    const singleTargetGroup = payload.targetGroups.length === 1;

    const configs = payload.configs.map((c, i) => {
      const body = {
        name: c.name,
        targetAppliedOn: c.targetAppliedOn,
        achievementBasis: c.achievementBasis,
        payoutBasis: c.payoutBasis,
        payoutType: c.payoutType,
        periodFrom: c.periodFrom || null,
        periodTo: c.periodTo || null,
        helpText: c.helpText || "",
      };
      if (!singleTargetGroup) {
        // Multiple target groups → server needs a reference. We use the
        // target group's position in this same payload as the reference;
        // verify this matches the live contract in swagger before relying
        // on it for anything beyond internal testing.
        body.targetGroupId = c.targetGroupId !== "" ? c.targetGroupId : 0;
      }
      if (c.payoutType === "PRICE_PROTECTION") {
        body.protection = {
          claimWindowDays: Number(c.protection?.claimWindowDays) || 0,
          ...(c.protection?.brandStatedClaimValuePerUnit !== ""
            ? { brandStatedClaimValuePerUnit: Number(c.protection.brandStatedClaimValuePerUnit) }
            : {}),
        };
      } else {
        body.slabs = (c.slabs || []).map((sl) => ({
          rangeFrom: Number(sl.rangeFrom),
          rangeTo: Number(sl.rangeTo),
          amount: Number(sl.amount),
          gift: c.payoutType === "GIFT_REWARD_TRIP" ? sl.gift || "" : null,
          remark: sl.remark || "",
          periodFrom: sl.periodFrom || null,
          periodTo: sl.periodTo || null,
          isDefaultSlab: c.payoutType === "PER_UNIT_POINTS" ? !!sl.isDefaultSlab : false,
        }));
      }
      return body;
    });

    return {
      name: payload.name,
      description: payload.description || "",
      imageUrl: payload.imageUrl || "",
      termsAndConditions: payload.termsAndConditions || "",
      brand: { id: payload.brandId, name: payload.brandName },
      periodFrom: payload.periodFrom,
      periodTo: payload.periodTo,
      groups: payload.groups.map((g) => ({ id: g.id, name: g.name })),
      ...(payload.retailerTypeId ? { retailerType: { id: payload.retailerTypeId, name: payload.retailerTypeName } } : {}),
      targetGroups,
      configs,
      source: { type: "ADMIN_MANUAL" },
    };
  };

  const validateBasics = () => {
    if (!payload.name?.trim()) return "Name is required";
    if (!payload.brandId) return "Brand is required";
    if (!payload.periodFrom || !payload.periodTo) return "Period From/To are required";
    if (payload.periodFrom > payload.periodTo) return "Period From must be before Period To";
    if (!payload.targetGroups.length) return "At least one Target Group is required";
    if (!payload.configs.length) return "At least one Payout Config is required";
    return null;
  };

  const runSave = (apiCall, successMessage) => {
    setSaving(true);
    apiCall
      .then(() => {
        showToast(successMessage, "success");
        onSaved();
        onClose();
      })
      .catch((err) => {
        showToast(err?.response?.data?.message || "Request failed");
      })
      .finally(() => setSaving(false));
  };

  const handleSaveDraft = () => {
    const error = validateBasics();
    if (error) return showToast(error);

    let requestPayload;
    try {
      requestPayload = buildRequestPayload();
    } catch (err) {
      return showToast(err.message);
    }

    const m = mode(editingScheme);
    if (m === "create") {
      runSave(SchemesV3Api.Create(requestPayload), "Scheme created as DRAFT");
    } else if (m === "edit") {
      runSave(SchemesV3Api.Update(payload.id, requestPayload), "Scheme updated");
    } else {
      runSave(SchemesV3Api.Amend(payload.id, requestPayload), "Active scheme amended (publishVersion +1)");
    }
  };

  const handleSubmitForReview = () => {
    if (!payload.id) return showToast("Save the scheme first");
    runSave(SchemesV3Api.Submit(payload.id), "Scheme submitted for review (IN_REVIEW)");
  };

  const handlePublish = () => {
    if (!payload.id) return showToast("Save the scheme first");
    runSave(SchemesV3Api.Publish(payload.id), "Scheme published (ACTIVE)");
  };

  const m = mode(editingScheme);
  const readOnly = m === "view";

  return (
    <Dialog fullScreen open={open} onClose={handleClose} TransitionComponent={Transition}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
            {m === "create"
              ? "Create V3 Scheme"
              : m === "amend"
              ? `Amend Active Scheme — ${payload.name}`
              : m === "view"
              ? `View Archived Scheme — ${payload.name}`
              : `Edit Scheme — ${payload.name}`}
          </Typography>
          {payload.status && <Chip label={payload.status} color={payload.status === "ACTIVE" ? "success" : "default"} size="small" sx={{ mr: 2 }} />}
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: { xs: 2, md: 4 }, ...(readOnly ? { pointerEvents: "none", opacity: 0.75 } : {}) }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Basics</Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              required
              label="Name"
              value={payload.name}
              onChange={(e) => setPayload((p) => ({ ...p, name: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Brand</InputLabel>
              <Select label="Brand" value={payload.brandId || ""} onChange={handleBrandChange} disabled={m !== "create"}>
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Period From"
              value={payload.periodFrom}
              onChange={(e) => setPayload((p) => ({ ...p, periodFrom: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Period To"
              value={payload.periodTo}
              onChange={(e) => setPayload((p) => ({ ...p, periodTo: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Image URL"
              value={payload.imageUrl}
              onChange={(e) => setPayload((p) => ({ ...p, imageUrl: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Retailer Type (optional)</InputLabel>
              <Select
                label="Retailer Type (optional)"
                value={payload.retailerTypeId || ""}
                onChange={(e) => {
                  const rt = retailerTypes.find((r) => r.id === e.target.value);
                  setPayload((p) => ({ ...p, retailerTypeId: e.target.value, retailerTypeName: rt?.name || "" }));
                }}
              >
                <MenuItem value="">None</MenuItem>
                {retailerTypes.map((r) => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <AutoComplete
              label="Groups (retailer groups this scheme applies to)"
              multiple
              value={payload.groups}
              getOptionLabel={(option) => option.name || ""}
              fetchOptions={fetchGroups}
              onChange={handleGroupsChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={payload.description}
              onChange={(e) => setPayload((p) => ({ ...p, description: e.target.value }))}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Terms and Conditions"
              value={payload.termsAndConditions}
              onChange={(e) => setPayload((p) => ({ ...p, termsAndConditions: e.target.value }))}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Target Groups</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addTargetGroup}>Add Target Group</Button>
        </Box>
        {payload.targetGroups.map((tg, i) => (
          <TargetGroupBuilder
            key={i}
            sessionKey={`scheme-tg-${i}-${open}`}
            targetGroup={tg}
            brandId={payload.brandId}
            templates={templates}
            showRemove={payload.targetGroups.length > 1}
            onChange={(v) => updateTargetGroup(i, v)}
            onRemove={() => removeTargetGroup(i)}
          />
        ))}

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Payout Configs</Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addConfig}>Add Config</Button>
        </Box>
        {payload.configs.map((c, i) => (
          <ConfigBuilder
            key={i}
            config={c}
            showRemove={payload.configs.length > 1}
            showTargetGroupSelect={payload.targetGroups.length > 1}
            targetGroups={payload.targetGroups.map((tg, idx) => ({ ...tg, _localId: idx }))}
            onChange={(v) => updateConfig(i, v)}
            onRemove={() => removeConfig(i)}
          />
        ))}
      </DialogContent>

      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={handleClose} disabled={saving}>{readOnly ? "Close" : "Cancel"}</Button>
        {!readOnly && (
          <Button variant="outlined" onClick={handleSaveDraft} disabled={saving}>
            {m === "create" ? "Save as Draft" : m === "amend" ? "Save Amendment" : "Save Changes"}
          </Button>
        )}
        {m === "edit" && payload.status === "DRAFT" && (
          <Button variant="outlined" color="secondary" onClick={handleSubmitForReview} disabled={saving}>
            Submit for Review
          </Button>
        )}
        {(m === "edit") && (
          <Button variant="contained" color="success" onClick={handlePublish} disabled={saving}>
            Publish
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
